import { useState } from "react";
import { Mail, X, Send, Check } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * Request-access modal used on the landing page.
 * Posts to /api/invite-requests (public, IP rate-limited).
 *
 * We deliberately keep this self-contained (no shared layout, no auth) so it
 * works for completely unauthenticated visitors. Uses fetch instead of the
 * `api` axios client because the latter carries auth interceptors not needed
 * here — and we want this to stay light on the marketing surface.
 */
export default function RequestAccessModal({ open, onClose }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  // GDPR Art. 6(1)(a) — un-checked by default (opt-in must be active).
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const reset = () => {
    setName(""); setEmail(""); setMessage("");
    setMarketingConsent(false);
    setDone(false); setError("");
  };

  const handleClose = () => {
    onClose?.();
    setTimeout(reset, 250);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const apiBase =
        typeof window !== "undefined" && process.env.REACT_APP_BACKEND_URL
          ? (() => {
              try {
                const envOrigin = new URL(process.env.REACT_APP_BACKEND_URL).origin;
                return envOrigin === window.location.origin
                  ? process.env.REACT_APP_BACKEND_URL
                  : window.location.origin;
              } catch {
                return process.env.REACT_APP_BACKEND_URL;
              }
            })()
          : (process.env.REACT_APP_BACKEND_URL || "");
      const r = await fetch(`${apiBase}/api/invite-requests`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          message: message.trim(),
          marketing_consent: marketingConsent,
        }),
      });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        throw new Error(data.detail || "Something went wrong. Please try again.");
      }
      setDone(true);
    } catch (err) {
      setError(err.message || "Network error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50"
            onClick={handleClose}
            data-testid="request-access-overlay"
          />
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none"
          >
            <div className="pointer-events-auto w-full max-w-md bg-[#0A0A0A] border border-white/[0.12] shadow-2xl">
              {/* Header */}
              <div className="flex items-center justify-between px-7 py-5 border-b border-white/[0.10]">
                <div className="flex items-center gap-2.5">
                  <Mail className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.6} />
                  <p className="text-[10px] tracking-[0.25em] uppercase text-[#D4AF37]/90 font-sans">
                    Request Access
                  </p>
                </div>
                <button
                  onClick={handleClose}
                  data-testid="request-access-close"
                  className="text-white/65 hover:text-white transition-colors"
                  aria-label="Close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Body */}
              <div className="px-7 py-6">
                {done ? (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center py-8"
                    data-testid="request-access-success"
                  >
                    <div className="w-12 h-12 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/30 flex items-center justify-center mx-auto mb-5">
                      <Check className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.8} />
                    </div>
                    <h3 className="font-serif text-xl text-white mb-2">Request received</h3>
                    <p className="text-white/65 text-sm leading-relaxed font-sans max-w-xs mx-auto">
                      Thanks for your interest. We'll review your request and reach out by email when access is approved.
                    </p>
                    <button
                      onClick={handleClose}
                      data-testid="request-access-done"
                      className="mt-7 text-[#D4AF37] hover:text-[#B5952F] text-xs tracking-[0.2em] uppercase transition-colors font-sans"
                    >
                      Close
                    </button>
                  </motion.div>
                ) : (
                  <>
                    <h3 className="font-serif text-xl text-white mb-2 leading-snug">
                      Join the EQUIA private beta
                    </h3>
                    <p className="text-white/60 text-sm leading-relaxed font-sans mb-6">
                      Tell us a bit about you and your portfolio. We're onboarding a small cohort of international investors and review every request personally.
                    </p>

                    {error && (
                      <div
                        className="bg-red-500/10 border border-red-500/20 text-red-300 px-4 py-2.5 text-xs mb-4 font-sans"
                        data-testid="request-access-error"
                      >
                        {error}
                      </div>
                    )}

                    <form onSubmit={submit} className="space-y-4">
                      <Field label="Full name">
                        <input
                          required
                          type="text"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          data-testid="request-access-name"
                          placeholder="Jane Doe"
                          className="w-full bg-[#050505] border border-white/[0.12] text-white px-4 py-2.5 text-sm font-sans placeholder:text-white/55 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none transition-all"
                        />
                      </Field>
                      <Field label="Email">
                        <input
                          required
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          data-testid="request-access-email"
                          placeholder="you@example.com"
                          className="w-full bg-[#050505] border border-white/[0.12] text-white px-4 py-2.5 text-sm font-sans placeholder:text-white/55 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none transition-all"
                        />
                      </Field>
                      <Field label="A bit about your portfolio (optional)">
                        <textarea
                          rows={3}
                          value={message}
                          onChange={(e) => setMessage(e.target.value)}
                          data-testid="request-access-message"
                          placeholder="e.g. 2 assets in Dubai, 1 in Germany, exploring North Cyprus next year"
                          className="w-full bg-[#050505] border border-white/[0.12] text-white px-4 py-2.5 text-sm font-sans placeholder:text-white/55 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none transition-all resize-none"
                          maxLength={600}
                        />
                      </Field>

                      {/* GDPR-compliant marketing opt-in — unchecked by default */}
                      <label className="flex items-start gap-3 cursor-pointer group select-none">
                        <input
                          type="checkbox"
                          checked={marketingConsent}
                          onChange={(e) => setMarketingConsent(e.target.checked)}
                          data-testid="request-access-marketing-consent"
                          className="mt-0.5 w-3.5 h-3.5 accent-[#D4AF37] bg-[#050505] cursor-pointer flex-shrink-0"
                        />
                        <span className="text-white/80 text-[11px] leading-relaxed font-sans group-hover:text-white/75 transition-colors">
                          Send me occasional product updates &amp; investor insights via email. Max ~1 per month, unsubscribe with one click anytime.
                        </span>
                      </label>

                      <button
                        type="submit"
                        disabled={loading}
                        data-testid="request-access-submit"
                        className="w-full bg-[#D4AF37] text-black py-3 text-sm font-medium tracking-wide hover:bg-[#B5952F] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {loading ? (
                          "Sending…"
                        ) : (
                          <>
                            <Send className="w-3.5 h-3.5" /> Request invite
                          </>
                        )}
                      </button>
                    </form>

                    <p className="text-white/60 text-[10px] tracking-wider leading-relaxed mt-5 font-sans">
                      By submitting, you agree we may store your email to process your invitation. We never share it. See our Privacy Policy.
                    </p>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] tracking-[0.15em] uppercase text-white/60 font-sans mb-2 block">
        {label}
      </label>
      {children}
    </div>
  );
}
